import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ChoiceButton } from "@/components/ChoiceButton";
import { Check, Clock3, GitMerge, Undo2 } from "lucide-react";

/* NAMES WAITING FOR APPROVAL (his call, 23 Oct): the crew suggest a tag name that isn't on the list and keep working;
   a list admin approves it with one tap, or merges it into a listed name with one tap — every tag using it follows.
   A merge can be put back. Nothing is deleted. */
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

function WaitingRow({ s, i, busy, act }) {
  return (
    <div data-testid={`ns-row-${i}`} className="flex flex-wrap items-center gap-2 bg-white border border-amber-200 rounded-lg px-3 py-2 text-sm">
      <Clock3 className="w-4 h-4 text-amber-600" />
      <b data-testid={`ns-name-${i}`}>{s.name}</b>
      <span className="text-[11px] text-slate-500">suggested by {(s.by || "").split("@")[0]} · {when(s.at)} · used on <b data-testid={`ns-tags-${i}`}>{s.tags}</b> tag(s), {s.parts} part(s)</span>
      <span className="ml-auto flex flex-wrap gap-1.5">
        <ChoiceButton tone="go" data-testid={`ns-approve-${i}`} disabled={busy} onClick={() => act(`/name-suggestions/${s.id}/approve`, null, `“${s.name}” is on the names list`)}>
          <Check className="w-3.5 h-3.5 mr-1" />Approve “{s.name}”
        </ChoiceButton>
        {s.closest.map((c) => (
          <ChoiceButton key={c} data-testid={`ns-merge-${i}-${c.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}`} disabled={busy}
            onClick={() => act(`/name-suggestions/${s.id}/merge`, { into: c }, `“${s.name}” merged into “${c}” — every tag using it follows`)}>
            <GitMerge className="w-3.5 h-3.5 mr-1" />Merge into {c}
          </ChoiceButton>
        ))}
      </span>
    </div>
  );
}

export function NameSuggestionsPanel() {
  const [data, setData] = useState(null);
  const [names, setNames] = useState([]);
  const [busy, setBusy] = useState(false);
  const [into, setInto] = useState({});
  const load = useCallback(() => { api.get("/name-suggestions").then(({ data: d }) => setData(d)).catch(() => setData({ waiting: [], merged: [] })); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/managed-lists/names").then(({ data: d }) => setNames((d.items || []).map((x) => x.name))).catch(() => {}); }, []);
  const act = async (path, body, ok) => {
    setBusy(true);
    try { await api.post(path, body); toast.success(ok); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false); load();
  };
  if (!data || (!data.waiting.length && !data.merged.length)) return null;
  return (
    <section data-testid="ns-panel" className="mb-5 rounded-xl border-2 border-amber-300 bg-amber-50/50 p-3 space-y-2 max-w-4xl">
      <h2 className="font-display font-bold text-[#1F1F1F]">Names waiting for approval <span data-testid="ns-count" className="font-mono text-sm text-amber-800">({data.total ?? data.waiting.length})</span></h2>
      {data.total > data.shown && <p data-testid="ns-showing" className="text-[12px] font-semibold text-amber-900">Showing the newest {data.shown} of {data.total}. Approve or merge these and the rest come up.</p>}
      <p className="text-[12px] text-slate-600">Tag names the crew suggested while naming tags in Ask AI. Each tag already uses the name. Approve it onto the names list, or merge it into a name you already have — every tag using it, and any part spun off under it, follows.</p>
      {data.waiting.map((s, i) => (
        <div key={s.id}>
          <WaitingRow s={s} i={i} busy={busy} act={act} />
          <div className="flex flex-wrap items-center gap-1.5 mt-1 ml-6 text-[12px] text-slate-500">
            Or merge into another name:
            <select data-testid={`ns-merge-pick-${i}`} value={into[s.id] || ""} onChange={(e) => setInto({ ...into, [s.id]: e.target.value })}
              className="h-9 rounded-lg border-[1.5px] border-slate-300 bg-white px-2 text-xs">
              <option value="">Pick a name on the list…</option>
              {names.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <ChoiceButton data-testid={`ns-merge-picked-${i}`} disabled={busy || !into[s.id]}
              onClick={() => act(`/name-suggestions/${s.id}/merge`, { into: into[s.id] }, `“${s.name}” merged into “${into[s.id]}”`)}>
              <GitMerge className="w-3.5 h-3.5 mr-1" />Merge
            </ChoiceButton>
          </div>
        </div>
      ))}
      {data.merged.length > 0 && (
        <div data-testid="ns-merged" className="pt-1 space-y-1">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Recently merged</div>
          {data.merged.map((s, i) => (
            <div key={s.id} data-testid={`ns-merged-${i}`} className="flex flex-wrap items-center gap-2 text-[13px] bg-white border border-slate-200 rounded-lg px-3 py-1.5">
              <span className="line-through text-[#B01E28]">{s.name}</span> → <b>{s.merged_into}</b>
              <span className="text-[11px] text-slate-500">{s.tags} tag(s) · by {(s.decided_by || "").split("@")[0]} · {when(s.decided_at)}</span>
              <ChoiceButton tone="danger" data-testid={`ns-merge-undo-${i}`} disabled={busy} className="ml-auto h-8 min-h-8"
                onClick={() => act(`/name-suggestions/${s.id}/undo`, null, `Put back — “${s.name}” is waiting again`)}>
                <Undo2 className="w-3 h-3 mr-1" />Put back
              </ChoiceButton>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
